import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Search,
  Plus,
  Link2,
  Copy,
  Eye,
  MousePointerClick,
  UserPlus,
  Percent,
  QrCode,
  ExternalLink,
  CheckCircle,
  Download,
} from 'lucide-react'

interface ReferralLink {
  id: string
  affiliateCode: string
  affiliateName: string
  linkCode: string
  linkUrl: string
  campaign: string
  clicks: number
  registrations: number
  conversionRate: number
  totalDeposit: number
  isActive: boolean
  createdAt: string
}

const mockLinks: ReferralLink[] = [
  {
    id: '1',
    affiliateCode: 'AFF001',
    affiliateName: 'สมชาย ใจดี',
    linkCode: 'SC2025',
    linkUrl: 'https://example.com/ref/SC2025',
    campaign: 'โปรโมทหลัก',
    clicks: 15820,
    registrations: 1250,
    conversionRate: 7.9,
    totalDeposit: 15800000,
    isActive: true,
    createdAt: '2024-01-15',
  },
  {
    id: '2',
    affiliateCode: 'AFF001',
    affiliateName: 'สมชาย ใจดี',
    linkCode: 'SC-FB',
    linkUrl: 'https://example.com/ref/SC-FB',
    campaign: 'Facebook Ads',
    clicks: 8500,
    registrations: 620,
    conversionRate: 7.3,
    totalDeposit: 5200000,
    isActive: true,
    createdAt: '2024-02-20',
  },
  {
    id: '3',
    affiliateCode: 'AFF002',
    affiliateName: 'วิชัย มั่งมี',
    linkCode: 'WC-MAIN',
    linkUrl: 'https://example.com/ref/WC-MAIN',
    campaign: 'โปรโมทหลัก',
    clicks: 12300,
    registrations: 856,
    conversionRate: 7.0,
    totalDeposit: 9500000,
    isActive: true,
    createdAt: '2024-02-20',
  },
  {
    id: '4',
    affiliateCode: 'AFF003',
    affiliateName: 'พิมพ์ใจ สวยงาม',
    linkCode: 'PJ-TT',
    linkUrl: 'https://example.com/ref/PJ-TT',
    campaign: 'TikTok',
    clicks: 6800,
    registrations: 425,
    conversionRate: 6.25,
    totalDeposit: 4200000,
    isActive: true,
    createdAt: '2024-03-10',
  },
  {
    id: '5',
    affiliateCode: 'AFF004',
    affiliateName: 'อนุชา รวยเร็ว',
    linkCode: 'AN-LINE',
    linkUrl: 'https://example.com/ref/AN-LINE',
    campaign: 'Line OA',
    clicks: 2500,
    registrations: 156,
    conversionRate: 6.24,
    totalDeposit: 1250000,
    isActive: false,
    createdAt: '2024-06-05',
  },
]

export function ReferralLinksPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLink, setSelectedLink] = useState<ReferralLink | null>(null)
  const [showQR, setShowQR] = useState<ReferralLink | null>(null)

  const filteredLinks = useMemo(() => {
    if (!searchTerm) return mockLinks
    const term = searchTerm.toLowerCase()
    return mockLinks.filter(l =>
      l.affiliateName.toLowerCase().includes(term) ||
      l.linkCode.toLowerCase().includes(term) ||
      l.campaign.toLowerCase().includes(term)
    )
  }, [searchTerm])

  const stats = useMemo(() => ({
    totalLinks: mockLinks.length,
    activeLinks: mockLinks.filter(l => l.isActive).length,
    totalClicks: mockLinks.reduce((sum, l) => sum + l.clicks, 0),
    totalRegistrations: mockLinks.reduce((sum, l) => sum + l.registrations, 0),
  }), [])

  const formatNumber = (num: number) => new Intl.NumberFormat('th-TH').format(num)
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    // Could add toast notification here
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">ลิงก์แนะนำ</h1>
          <p className="text-muted-foreground">จัดการลิงก์แนะนำสำหรับพันธมิตร</p>
        </div>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          สร้างลิงก์ใหม่
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-primary/10">
                <Link2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ลิงก์ทั้งหมด</p>
                <p className="text-xl font-bold">{stats.totalLinks}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-win/10">
                <CheckCircle className="h-5 w-5 text-win" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ใช้งาน</p>
                <p className="text-xl font-bold text-win">{stats.activeLinks}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <MousePointerClick className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">คลิกรวม</p>
                <p className="text-xl font-bold">{formatNumber(stats.totalClicks)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-purple-500/10">
                <UserPlus className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">สมัครรวม</p>
                <p className="text-xl font-bold">{formatNumber(stats.totalRegistrations)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Links Table */}
      <Card>
        <CardHeader className="pb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="ค้นหา รหัส, พันธมิตร, แคมเปญ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-y">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">พันธมิตร</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">ลิงก์</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">แคมเปญ</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">คลิก</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">สมัคร</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">Conv.</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">ยอดฝาก</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">สถานะ</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {filteredLinks.map((link) => (
                  <tr key={link.id} className="border-b hover:bg-muted/30">
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-medium">{link.affiliateName}</p>
                        <p className="text-xs text-muted-foreground">{link.affiliateCode}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="font-mono bg-muted">
                          {link.linkCode}
                        </Badge>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7"
                          onClick={() => copyToClipboard(link.linkUrl)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Badge variant="outline">{link.campaign}</Badge>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <MousePointerClick className="h-3 w-3 text-muted-foreground" />
                        <span className="font-medium">{formatNumber(link.clicks)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <UserPlus className="h-3 w-3 text-win" />
                        <span className="font-medium text-win">{formatNumber(link.registrations)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Badge variant="secondary" className="bg-primary/10 text-primary border-0">
                        {link.conversionRate}%
                      </Badge>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-medium">{formatCurrency(link.totalDeposit)}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Switch checked={link.isActive} />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => setSelectedLink(link)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => setShowQR(link)}
                        >
                          <QrCode className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => window.open(link.linkUrl, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Link Detail Dialog */}
      <Dialog open={!!selectedLink} onOpenChange={() => setSelectedLink(null)}>
        <DialogContent className="max-w-lg">
          {selectedLink && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5" />
                  รายละเอียดลิงก์
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="p-3 rounded-lg bg-muted/30 flex items-center gap-2">
                  <code className="text-sm flex-1 break-all">{selectedLink.linkUrl}</code>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => copyToClipboard(selectedLink.linkUrl)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">พันธมิตร</p>
                    <p className="font-medium">{selectedLink.affiliateName}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">แคมเปญ</p>
                    <Badge variant="outline">{selectedLink.campaign}</Badge>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3 pt-4 border-t">
                  <div className="text-center p-3 rounded-lg bg-blue-500/10">
                    <MousePointerClick className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                    <p className="text-lg font-bold">{formatNumber(selectedLink.clicks)}</p>
                    <p className="text-xs text-muted-foreground">คลิก</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-win/10">
                    <UserPlus className="h-5 w-5 text-win mx-auto mb-1" />
                    <p className="text-lg font-bold text-win">{formatNumber(selectedLink.registrations)}</p>
                    <p className="text-xs text-muted-foreground">สมัคร</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-purple-500/10">
                    <Percent className="h-5 w-5 text-purple-500 mx-auto mb-1" />
                    <p className="text-lg font-bold">{selectedLink.conversionRate}%</p>
                    <p className="text-xs text-muted-foreground">Conv.</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-primary/10">
                    <p className="text-lg font-bold">{formatCurrency(selectedLink.totalDeposit)}</p>
                    <p className="text-xs text-muted-foreground">ยอดฝาก</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button className="flex-1 gap-2" onClick={() => copyToClipboard(selectedLink.linkUrl)}>
                    <Copy className="h-4 w-4" /> คัดลอกลิงก์
                  </Button>
                  <Button variant="outline" className="gap-2" onClick={() => setShowQR(selectedLink)}>
                    <QrCode className="h-4 w-4" /> QR Code
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={!!showQR} onOpenChange={() => setShowQR(null)}>
        <DialogContent className="max-w-sm text-center">
          {showQR && (
            <>
              <DialogHeader>
                <DialogTitle>QR Code</DialogTitle>
                <DialogDescription>{showQR.linkCode}</DialogDescription>
              </DialogHeader>
              <div className="py-6">
                <div className="h-48 w-48 mx-auto bg-muted rounded-lg flex items-center justify-center">
                  <QrCode className="h-32 w-32 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground mt-4 break-all">{showQR.linkUrl}</p>
              </div>
              <Button className="w-full gap-2">
                <Download className="h-4 w-4" /> ดาวน์โหลด QR Code
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

